import React, { useState, useMemo, useRef } from 'react';
import { FiRefreshCw, FiDownload, FiSearch, FiAlertCircle, FiShield, FiHardDrive } from 'react-icons/fi';
import { getFabricActivityEvents, type FabricActivityEvent } from '../../api/fabric';

// ─── Operation metadata ────────────────────────────────────────────────────────

const OPERATION_META: Record<string, { label: string; color: string; bg: string; group: string }> = {
  // Consultation
  ViewReport:          { label: 'Consulter rapport',      color: '#2563EB', bg: '#EFF6FF', group: 'Lecture' },
  ViewDashboard:       { label: 'Consulter dashboard',    color: '#2563EB', bg: '#EFF6FF', group: 'Lecture' },
  GetDataset:          { label: 'Lire dataset',           color: '#2563EB', bg: '#EFF6FF', group: 'Lecture' },
  ExportArtifact:      { label: 'Exporter',               color: '#7C3AED', bg: '#F5F3FF', group: 'Export'  },
  ExportReport:        { label: 'Exporter rapport',       color: '#7C3AED', bg: '#F5F3FF', group: 'Export'  },
  // Création
  CreateItem:          { label: 'Créer item',             color: '#059669', bg: '#ECFDF5', group: 'Création' },
  CreateReport:        { label: 'Créer rapport',          color: '#059669', bg: '#ECFDF5', group: 'Création' },
  CreateDataset:       { label: 'Créer dataset',          color: '#059669', bg: '#ECFDF5', group: 'Création' },
  // Modification
  EditItem:            { label: 'Modifier item',          color: '#D97706', bg: '#FEF3C7', group: 'Modification' },
  UpdateItem:          { label: 'Mettre à jour',          color: '#D97706', bg: '#FEF3C7', group: 'Modification' },
  PatchItem:           { label: 'Patcher item',           color: '#D97706', bg: '#FEF3C7', group: 'Modification' },
  // Suppression
  DeleteItem:          { label: 'Supprimer item',         color: '#DC2626', bg: '#FEF2F2', group: 'Suppression' },
  DeleteReport:        { label: 'Supprimer rapport',      color: '#DC2626', bg: '#FEF2F2', group: 'Suppression' },
  // Exécution
  RunNotebook:         { label: 'Exécuter notebook',      color: '#0891B2', bg: '#ECFEFF', group: 'Exécution' },
  RunPipeline:         { label: 'Exécuter pipeline',      color: '#0891B2', bg: '#ECFEFF', group: 'Exécution' },
  RunDataflow:         { label: 'Exécuter dataflow',      color: '#0891B2', bg: '#ECFEFF', group: 'Exécution' },
  RefreshDataset:      { label: 'Rafraîchir dataset',     color: '#0891B2', bg: '#ECFEFF', group: 'Exécution' },
  // Partage / Accès
  ShareReport:         { label: 'Partager rapport',       color: '#6B7280', bg: '#F9FAFB', group: 'Accès' },
  AddGroupMembers:     { label: 'Ajouter membres',        color: '#6B7280', bg: '#F9FAFB', group: 'Accès' },
  UpdateWorkspaceAccess: { label: 'Modifier accès WS',   color: '#6B7280', bg: '#F9FAFB', group: 'Accès' },
  // Stockage OneLake/ADLS (opérations fichier bas niveau)
  CreateFile:          { label: 'Créer fichier',          color: '#059669', bg: '#ECFDF5', group: 'Stockage' },
  DeleteFile:          { label: 'Supprimer fichier',      color: '#DC2626', bg: '#FEF2F2', group: 'Stockage' },
  DeleteFileOrBlob:    { label: 'Supprimer blob',         color: '#DC2626', bg: '#FEF2F2', group: 'Stockage' },
  UploadFile:          { label: 'Uploader fichier',       color: '#D97706', bg: '#FEF3C7', group: 'Stockage' },
  WriteFile:           { label: 'Écrire fichier',         color: '#D97706', bg: '#FEF3C7', group: 'Stockage' },
  ReadArtifact:        { label: 'Lire artefact',          color: '#2563EB', bg: '#EFF6FF', group: 'Stockage' },
};

function opMeta(op: string) {
  return OPERATION_META[op] ?? { label: op, color: '#374151', bg: '#F3F4F6', group: 'Autre' };
}

const STORAGE_OPS = new Set([
  'CreateFile', 'DeleteFile', 'DeleteFileOrBlob', 'UploadFile', 'WriteFile',
  'ReadArtifact', 'MountFolder', 'UnmountFolder',
]);

function StorageBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 7px', borderRadius: 4,
      background: '#F1F5F9', border: '1px solid #CBD5E1',
      color: '#475569', fontSize: 10, fontWeight: 600,
    }}>
      <FiHardDrive size={10} />
      OneLake
    </span>
  );
}

// ─── Date presets ──────────────────────────────────────────────────────────────

const PRESETS = [
  { label: '1h',  hours: 1   },
  { label: '24h', hours: 24  },
  { label: '3j',  hours: 72  },
  { label: '7j',  hours: 168 },
];

function presetDates(hours: number) {
  const end = new Date();
  const start = new Date(end.getTime() - hours * 60 * 60 * 1000);
  return {
    startDate: start.toISOString(),
    endDate:   end.toISOString(),
  };
}

// ─── Component ─────────────────────────────────────────────────────────────────

const AuditLogTab: React.FC = () => {
  const [preset, setPreset]         = useState(1); // index in PRESETS (24h)
  const [events, setEvents]         = useState<FabricActivityEvent[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [hint, setHint]             = useState<string | null>(null);
  const [fetchedAt, setFetchedAt]   = useState<string | null>(null);

  const [filterUser, setFilterUser]   = useState('');
  const [filterOp, setFilterOp]       = useState('');
  const [filterWs, setFilterWs]       = useState('');
  const [search, setSearch]           = useState('');
  const [hideSystem, setHideSystem]   = useState(true);
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(50);
  const initialLoadDone               = useRef(false);

  const load = async (presetIdx?: number) => {
    const idx = presetIdx ?? preset;
    setLoading(true);
    setError(null);
    setHint(null);
    try {
      const { startDate, endDate } = presetDates(PRESETS[idx].hours);
      const res = await getFabricActivityEvents({ startDate, endDate });
      setEvents(res.events);
      setFetchedAt(res.fetchedAt);
    } catch (err: any) {
      const data = err?.response?.data;
      const detail = data?.details ? ` — ${data.details}` : '';
      setError((data?.error || 'Erreur lors du chargement des activités') + detail);
      if (data?.hint) setHint(data.hint);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Unique values for filter dropdowns
  const uniqueOps = useMemo(() => Array.from(new Set(events.map(e => e.operation))).sort(), [events]);
  const uniqueWs  = useMemo(() => Array.from(new Set(events.map(e => e.workspaceName).filter(Boolean))).sort(), [events]);

  // Auto-load on mount with default preset (24h) — ref évite le double-fire de React Strict Mode
  React.useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    load(1);
  }, []);

  // Reset page when filters change
  React.useEffect(() => { setPage(1); }, [filterOp, filterWs, filterUser, search, hideSystem, events]);

  // Filtered events
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return events.filter(e => {
      if (hideSystem && e.userId === 'System') return false;
      if (filterOp && e.operation !== filterOp) return false;
      if (filterWs && e.workspaceName !== filterWs) return false;
      if (filterUser && !e.userId.toLowerCase().includes(filterUser.toLowerCase())) return false;
      if (q && !e.userId.toLowerCase().includes(q) &&
               !e.operation.toLowerCase().includes(q) &&
               !e.workspaceName.toLowerCase().includes(q) &&
               !e.itemName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [events, filterOp, filterWs, filterUser, search, hideSystem]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

  // KPI counts by group
  const kpis = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach(e => {
      const g = opMeta(e.operation).group;
      counts[g] = (counts[g] || 0) + 1;
    });
    return counts;
  }, [filtered]);

  const exportCSV = () => {
    const header = ['Heure', 'Utilisateur', 'Opération', 'Workspace', 'Item', 'Type'].join(';');
    const rows = filtered.map(e => [
      new Date(e.creationTime).toLocaleString('fr-FR'),
      e.userId,
      e.operation,
      e.workspaceName,
      e.itemName,
      e.itemType,
    ].map(v => `"${v}"`).join(';'));
    const blob = new Blob([header + '\n' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fabric-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (iso: string) => new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Header controls ─────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        {/* Preset buttons */}
        <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 3, gap: 2 }}>
          {PRESETS.map((p, i) => (
            <button
              key={p.label}
              onClick={() => { setPreset(i); load(i); }}
              style={{
                padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
                fontWeight: preset === i ? 700 : 400,
                background: preset === i ? 'white' : 'transparent',
                color: preset === i ? '#111827' : '#6B7280',
                boxShadow: preset === i ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>max 7j</span>

        <button
          onClick={() => load()}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 16px', borderRadius: 8, border: '1px solid #E5E7EB',
            background: '#1E3A5F', color: 'white', cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: 13, fontWeight: 600,
          }}
        >
          <FiRefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Chargement…' : 'Charger les activités'}
        </button>

        {events.length > 0 && (
          <>
            {/* Search */}
            <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
              <FiSearch size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                style={{ width: '100%', padding: '7px 10px 7px 28px', border: '1px solid #E5E7EB', borderRadius: 7, fontSize: 12 }}
              />
            </div>

            {/* Operation filter */}
            <select value={filterOp} onChange={e => setFilterOp(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 7, fontSize: 12, background: 'white' }}>
              <option value="">Toutes les opérations</option>
              {uniqueOps.map(op => <option key={op} value={op}>{opMeta(op).label || op}</option>)}
            </select>

            {/* Workspace filter */}
            <select value={filterWs} onChange={e => setFilterWs(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 7, fontSize: 12, background: 'white' }}>
              <option value="">Tous les workspaces</option>
              {uniqueWs.map(ws => <option key={ws} value={ws}>{ws}</option>)}
            </select>

            {/* User search */}
            <input
              value={filterUser}
              onChange={e => setFilterUser(e.target.value)}
              placeholder="Filtrer par utilisateur…"
              style={{ padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 7, fontSize: 12, minWidth: 170 }}
            />

            {/* Hide system toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', color: '#374151' }}>
              <input
                type="checkbox"
                checked={hideSystem}
                onChange={e => setHideSystem(e.target.checked)}
                style={{ accentColor: '#1E3A5F' }}
              />
              Masquer système
            </label>

            {/* Export */}
            <button onClick={exportCSV}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8,
                border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', fontSize: 12 }}>
              <FiDownload size={13} />
              CSV
            </button>
          </>
        )}
      </div>

      {/* ── Error / hint ────────────────────────────────── */}
      {error && (
        <div style={{ padding: '14px 16px', background: '#FEF2F2', border: '1px solid #FECACA',
          borderRadius: 8, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <FiAlertCircle size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontWeight: 600, color: '#991B1B', fontSize: 13 }}>{error}</div>
            {hint && <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>{hint}</div>}
          </div>
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────── */}
      {!loading && !error && events.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: '#9CA3AF' }}>
          <FiShield size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Aucune activité chargée</div>
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            Sélectionnez une période et cliquez sur « Charger les activités ».<br />
            Prérequis : permission <strong>ActivityFeed.Read</strong> (Office 365 Management APIs)<br />
            accordée avec consentement admin dans Azure AD → App registrations → API permissions.
          </div>
        </div>
      )}

      {/* ── KPI bar ─────────────────────────────────────── */}
      {events.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ padding: '8px 14px', background: '#F0FDFE', border: '1px solid #038387',
            borderRadius: 8, fontSize: 12, color: '#0F5256', fontWeight: 600 }}>
            {filtered.length} événement{filtered.length > 1 ? 's' : ''}
            {filtered.length < events.length && ` / ${events.length} total`}
          </div>
          {Object.entries(kpis).map(([group, count]) => (
            <div key={group} style={{ padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB',
              borderRadius: 8, fontSize: 11, color: '#374151' }}>
              <strong>{count}</strong> {group}
            </div>
          ))}
          {fetchedAt && (
            <div style={{ marginLeft: 'auto', fontSize: 10, color: '#9CA3AF', alignSelf: 'center' }}>
              Actualisé à {fmt(fetchedAt)}
            </div>
          )}
        </div>
      )}

      {/* ── Events table ────────────────────────────────── */}
      {filtered.length > 0 && (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E5E7EB' }}>
                {['Heure', 'Utilisateur', 'Opération', 'Workspace', 'Item', 'Type'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((e, idx) => {
                const meta = opMeta(e.operation);
                return (
                  <tr key={e.id || idx}
                    style={{ borderBottom: '1px solid #F3F4F6', background: idx % 2 === 0 ? 'white' : '#FAFAFA' }}>
                    <td style={{ padding: '8px 12px', color: '#6B7280', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(e.creationTime)}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#111827', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.userId}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 5,
                        background: meta.bg, color: meta.color, fontWeight: 600, fontSize: 11,
                      }}>
                        {meta.label}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#374151', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.workspaceName
                        ? e.workspaceName
                        : STORAGE_OPS.has(e.operation)
                          ? <StorageBadge />
                          : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#374151', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.itemName || <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#6B7280', fontSize: 11 }}>
                      {e.itemType || <span style={{ color: '#9CA3AF' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ──────────────────────────────────── */}
      {filtered.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
          {/* Rows per page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6B7280' }}>
            <span>Lignes par page :</span>
            {[25, 50, 100, 200].map(n => (
              <button
                key={n}
                onClick={() => { setPageSize(n); setPage(1); }}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB',
                  background: pageSize === n ? '#1E3A5F' : 'white',
                  color: pageSize === n ? 'white' : '#374151',
                  fontWeight: pageSize === n ? 700 : 400,
                  cursor: 'pointer', fontSize: 12,
                }}
              >
                {n}
              </button>
            ))}
          </div>

          {/* Page info + nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6B7280' }}>
            <span>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} sur {filtered.length}
            </span>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB',
                background: 'white', cursor: page === 1 ? 'not-allowed' : 'pointer',
                color: page === 1 ? '#D1D5DB' : '#374151', fontSize: 13,
              }}
            >
              ‹
            </button>
            {/* Page number pills */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | '…')[]>((acc, p, i, arr) => {
                if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '…'
                  ? <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#9CA3AF' }}>…</span>
                  : <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      style={{
                        padding: '4px 9px', borderRadius: 6, border: '1px solid #E5E7EB',
                        background: page === p ? '#1E3A5F' : 'white',
                        color: page === p ? 'white' : '#374151',
                        fontWeight: page === p ? 700 : 400,
                        cursor: 'pointer', fontSize: 12, minWidth: 30,
                      }}
                    >
                      {p}
                    </button>
              )}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB',
                background: 'white', cursor: page === totalPages ? 'not-allowed' : 'pointer',
                color: page === totalPages ? '#D1D5DB' : '#374151', fontSize: 13,
              }}
            >
              ›
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AuditLogTab;
